/**
 * /waves — public "browse the panel" page. Lets anonymous visitors see
 * every currently-open survey (admin toggle: Site Content → Public site
 * navigation → "Show 'Waves' menu item"). Clicking a card:
 *   - anonymous visitor  → toast + redirect to /register (sign-up prompt)
 *   - logged-in patron   → same gate the dashboard uses (take survey if
 *                          profile complete, else send to /panelist/profile)
 *   - anyone else (admin/subscriber/enterprise) → just goes to /panelist
 * No question content or per-patron sector matching is exposed here — this
 * is a discovery/marketing surface, not the real survey-taking flow.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLanguage } from "../hooks/useLanguage";
import { useSectorLabels } from "../lib/useSectorLabels";
import { OuterNav } from "../layouts/PublicNav";
import SeoPageHead from "../components/SeoPageHead";
import OodelXFooter from "../components/OodelXFooter";
import { ListChecks, ClockCountdown, ArrowRight } from "@phosphor-icons/react";

export default function PublicWaves() {
  const [waves, setWaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { lang, t } = useLanguage();
  const { sectorLabel } = useSectorLabels();
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/public/waves?lang=${lang}`).then((r) => setWaves(r.data || [])).finally(() => setLoading(false));
  }, [lang]);

  const isPatron = user && (user.role === "panelist" || user.role === "patron");
  const profileComplete = isPatron && !!user.profile?.profile_complete && (user.profile?.sectors_of_interest || []).length >= 3;

  const openWave = (w) => {
    if (!user) {
      nav(`/register?next=${encodeURIComponent("/panelist/waves")}`);
      return;
    }
    if (isPatron) {
      if (profileComplete) nav(`/panelist/survey/${w.id}`);
      else { toast.error("Complete your profile first to take surveys."); nav("/panelist/profile"); }
      return;
    }
    nav("/panelist");
  };

  return (
    <div className="min-h-screen bg-white themable-public">
      <SeoPageHead
        pageKey="waves"
        path="/waves"
        defaultTitle="Waves — Open Surveys | oodel."
        defaultDescription="Browse every currently open survey on oodel.'s Pakistan consumer panel. Sign up to take part and earn rewards."
      />
      <OuterNav />
      <div className="max-w-[1400px] mx-auto px-6 py-12" dir={lang === "ur" ? "rtl" : "ltr"}>
        <div className="border-b border-neutral-200 pb-6 mb-10">
          <div className="overline mb-2 text-mango">SECTION — WAVES</div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-neutral-900">
            {lang === "ur" ? "دستیاب سروے" : "Open surveys"}
          </h1>
          <p className="text-neutral-500 mt-3 max-w-2xl">
            {lang === "ur"
              ? "ابھی جاری تمام سروے دیکھیں۔ حصہ لینے اور انعامات کمانے کے لیے سائن اپ کریں۔"
              : "Every survey currently open on the oodel. panel. Sign up to take part and earn rewards for your responses."}
          </p>
        </div>

        {loading && <div className="text-neutral-500 p-8" data-testid="public-waves-loading">Loading…</div>}
        {!loading && waves.length === 0 && (
          <div className="border border-dashed border-neutral-300 rounded-2xl p-12 text-center text-neutral-500" data-testid="public-waves-empty">
            {lang === "ur" ? "ابھی کوئی سروے دستیاب نہیں۔" : "No surveys are open right now — check back soon."}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="public-waves-grid">
          {waves.map((w) => {
            const daysLeft = w.end_date ? Math.ceil((new Date(w.end_date) - new Date()) / 86400000) : null;
            return (
              <button
                key={w.id}
                onClick={() => openWave(w)}
                data-testid={`public-wave-${w.id}`}
                className="text-left bg-white border border-neutral-200 rounded-2xl shadow-sm hover:shadow-md hover:border-mango/40 hover:-translate-y-0.5 transition-all overflow-hidden flex flex-col group"
              >
                <div className="aspect-[4/5] bg-emerald-50 overflow-hidden flex items-center justify-center">
                  {w.cover_image ? (
                    <img src={w.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <ListChecks size={28} className="text-mango/40" />
                  )}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  {w.is_featured && (
                    <span className="self-start mb-2 text-[10px] font-bold uppercase tracking-wider bg-mango/10 text-mango px-2 py-0.5 rounded-full">
                      {lang === "ur" ? "نمایاں" : "Featured"}
                    </span>
                  )}
                  {w.sector_key && <div className="text-[10px] uppercase tracking-wide font-mono text-neutral-400 mb-1">{sectorLabel(w.sector_key, lang)}</div>}
                  <h3 className="text-base font-bold leading-snug text-neutral-900 group-hover:text-mango transition-colors line-clamp-2">{w.name}</h3>
                  {w.intro_text && <p className="text-xs text-neutral-500 mt-1.5 line-clamp-2">{w.intro_text}</p>}
                  <div className="mt-auto pt-3">
                  <div className="pt-3 border-t border-neutral-100 flex items-center justify-between text-xs text-neutral-500">
                    <span className="flex items-center gap-1.5"><ListChecks size={14} /> {w.question_count} {lang === "ur" ? "سوالات" : "questions"}</span>
                    {daysLeft !== null && daysLeft >= 0 && (
                      <span className="flex items-center gap-1 text-red-600 font-medium"><ClockCountdown size={13} weight="bold" /> {daysLeft === 0 ? (lang === "ur" ? "آج بند" : "closes today") : `${daysLeft}d`}</span>
                    )}
                  </div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-mango">
                    {lang === "ur" ? "سائن اَپ کریں" : "Sign up to take this survey"} <ArrowRight size={13} className="rtl-flip-arrow" />
                  </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <OodelXFooter />
    </div>
  );
}
